<template>
  <div class="p-relative">
    <div :ref="bodyKey" :title="ts" tabindex="0" class="message__body">
      <div
        :class="[
          {
            '--single': isSingleImage,
            '--feed': isFeed,
            'body-video': isVideo,
            'body-image': isImage,
            'body-attachment': isAttachment,
            'body-text': isText
          },
          'body'
        ]"
        @touchstart="e => $emit('touchstart', e)"
        @touchend="e => $emit('touchend', e)"
        @mousedown="e => $emit('mousedown', e)"
        @mouseup="e => $emit('mouseup', e)"
      >
        <template v-if="isVideo">
          <video-embed relative auto-initialize :video="videoData" />
        </template>
        <template v-else-if="isImage">
          <img
            v-for="(url, i) in imageUrls"
            :key="i"
            :src="url"
            class="w-100 h-100 mklbItem"
            :id="generateImageKeyForMessage(ts, i)"
            :data-gallery="`image-gallery-${messageThreadId}`"
            @click="handleSelectImage"
          />
        </template>
        <attachment
          v-else-if="isAttachment"
          :message="message"
          :attachment="message.attachment_data"
          :is-feed="isFeed"
          :user="user"
        />
        <template v-else>
          <span v-html="formattedText" />
        </template>
      </div>
    </div>

    <v-button
      v-if="!isFeed && hasReactions"
      light
      raised
      x-small
      :class="
        `
          ${reactionCount === initialReactionCount ? '--no-animation' : ''}
            ${reactionAtEnd ? '--at-end' : ''}
          py-0 px-1 reaction-count p-absolute
          `
      "
      @click="() => $emit('showReactions')"
    >
      <div :key="reactionCount" class="d-flex align-items-center">
        <v-icon icon="favorite" size="0.75rem"></v-icon>
        <div v-if="reactionCount > 1" class="reaction-count__number">
          {{ reactionCount }}
        </div>
      </div>
    </v-button>
  </div>
</template>

<script>
// TODO: can use this package for mention user in message
// Refer: https://pggalaviz.github.io/vue-highlights/#/
// For mentioning with dots:
// + https://github.com/pggalaviz/vue-highlights/issues/1
// + https://github.com/pggalaviz/vue-highlights/pull/38/files
import { autoLink } from "vue-highlights"

import { PROVIDERS } from "@/lib/video-utils"
import { isPresent } from "@/lib/utils"
import enumValidator from "@/lib/validators/enum-validator"
import { MESSAGE_TYPES } from "@/lib/pusher-utils"
import { generateImageKeyForMessage } from "@/lib/mk-lightbox"

export default {
  components: {
    VideoEmbed: () => import("@/components/VideoEmbed"),
    Attachment: () => import("@/components/message-thread/Attachment")
  },

  props: {
    messageThreadId: { type: [Number, String], required: true },
    message: { type: Object, required: true },
    isSelf: { type: Boolean, default: false },
    isFeed: { type: Boolean, default: false },
    type: {
      type: String,
      required: true,
      validator: enumValidator(Object.values(MESSAGE_TYPES))
    }
  },

  data() {
    return {
      initialReactionCount: 0
    }
  },

  computed: {
    bodyKey() {
      return `body-${this.ts}`
    },

    text() {
      return isPresent(this.message) ? this.message.message : ""
    },

    formattedText() {
      return autoLink(this.text, {
        extractUrlsWithoutProtocol: true,
        targetBlank: true,
        urlClass: "message-url"
      })
    },

    ts() {
      return isPresent(this.message) ? this.message.message_ts : null
    },

    user() {
      return isPresent(this.message.user) ? this.message.user : null
    },

    reactionCount() {
      return isPresent(this.message) ? this.message.reaction_count : 0
    },

    hasReactions() {
      return this.reactionCount > 0
    },

    isVideo() {
      return this.type === MESSAGE_TYPES.VIDEO
    },

    isImage() {
      return this.type === MESSAGE_TYPES.IMAGE
    },

    isAttachment() {
      return this.type === MESSAGE_TYPES.ATTACHMENT
    },

    isText() {
      return this.type === MESSAGE_TYPES.MESSAGE
    },

    imageUrls() {
      return isPresent(this.message) ? this.message.image_urls : []
    },

    videoUrl() {
      return isPresent(this.message) && this.isVideo
        ? this.message.video_url
        : null
    },

    isSingleImage() {
      return this.isImage && this.imageUrls.length === 1
    },

    evenNumberOfImages() {
      return this.isImage && this.imageUrls.length % 2 === 0
    },

    videoData() {
      return this.isVideo && isPresent(this.message)
        ? {
            provider: PROVIDERS.MUX,
            embed_link: this.videoUrl
          }
        : null
    },

    attachmentData() {
      return isPresent(this.message) ? this.message.attachment_data : {}
    },

    reactionAtEnd() {
      return this.evenNumberOfImages || this.isSingleImage || !this.isImage
    }
  },

  methods: {
    isPresent,
    generateImageKeyForMessage,

    handleSelectImage(e) {
      this.$emit("selectImage", e.target.id)
    }
  },

  mounted() {
    this.initialReactionCount = this.reactionCount
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables";
@import "@/assets/styles/mixins";

.message__body {
  position: relative;
  font-size: $size--4;
  margin-left: $size--2;
  word-break: break-word;

  &:hover {
    cursor: pointer;
  }

  .body {
    &:not(.--feed) {
      @include clickable;

      animation: fade-in-up 0.2s ease;
    }

    overflow: hidden;
    border-radius: 4px;
    border-top-right-radius: 16px;
    border-bottom-right-radius: 16px;
  }

  .body-text {
    padding: $size--2 $size--3;
    background-color: $color-ui--grey-20;
    color: $color-ui--grey-90;
    white-space: pre-line;
    max-width: $message-text--max-width;
  }

  .body-attachment {
    width: $message-attachment--width;
    overflow: visible;
  }

  .body-image {
    display: flex;
    flex-wrap: wrap;

    img {
      width: 9rem;
      height: 9rem;
      border-radius: 16px;
      object-fit: cover;
      margin-left: 0.15rem;
      margin-top: 0.15rem;
    }

    &.--single {
      max-width: 30rem;

      img {
        width: auto;
        height: auto;
        max-height: 22rem;
      }
    }
  }
}

.reaction-count {
  bottom: -$size--3;
  left: auto;
  right: 50%;
  z-index: 10;

  overflow: visible;

  .v-icon {
    animation: heart-react 1s ease-in-out;
  }

  &.--no-animation {
    .v-icon {
      animation: none;
    }
  }

  &.--at-end {
    right: 0;
  }

  &__number {
    margin-left: 0.2rem;
  }

  &.v-button {
    font-size: $font-size--x-small;
    font-weight: bold;
    min-height: $size--5;
  }

  .v-icon {
    color: $color-ui--red;
    top: 0px;
  }
}
</style>

<style lang="scss">
@import "@/assets/styles/variables";

.message__body .body {
  &.body-text a {
    color: inherit;
  }

  &.body-attachment {
    .attachment-image {
      background-color: $color-ui--grey-20;

      &.subscriber-avatar {
        padding: 1rem 0 1rem 1rem;
        background-color: $color-ui--white;

        img {
          object-fit: cover;
          width: 2rem;
          height: 2rem;
        }
      }

      img {
        display: block;
        width: 100%;
        max-height: 190px;
        border-radius: 2px;
        object-fit: contain;
      }
    }
  }

  &.body-video {
    .play-icon {
      background: rgba($color-ui--black, 0.56);
      border-radius: 50%;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }

    .video-embed .plyr.plyr--video {
      &:fullscreen,
      &:-webkit-full-screen {
        video {
          width: 100%;
          min-height: unset;
          max-height: unset;
        }
      }
    }

    .video-embed__video {
      position: relative;

      > .plyr {
        border-radius: 16px;

        .plyr__video-wrapper video {
          max-height: 300px;
        }
      }
    }
  }
}
</style>
